"use client";

import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhosphorIcon } from "@/components/marketing/PhosphorIcon";
import { trackAnalyticsEvent } from "@/lib/analytics/track";
import { cn } from "@/lib/utils";

export interface IBlogNewsletterProps {
  variant?: "sidebar" | "blog-cta";
  className?: string;
}

export function BlogNewsletter({
  variant = "sidebar",
  className,
}: IBlogNewsletterProps): React.ReactElement {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const surface = variant === "blog-cta" ? "blog_cta" : "blog_sidebar";

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    try {
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(body.error ?? "Something went wrong. Please try again.");
      }

      setStatus("success");
      trackAnalyticsEvent("submit_newsletter", { surface });
      setEmail("");
    } catch (err) {
      setStatus("error");
      setErrorMessage(
        err instanceof Error ? err.message : "Something went wrong."
      );
      trackAnalyticsEvent("newsletter_error", {
        error_code: err instanceof Error ? err.message : "unknown",
        surface,
      });
    }
  }

  if (status === "success") {
    return (
      <div
        className={cn(
          "rounded-xl border border-primary/20 bg-primary/5 p-5",
          className
        )}
      >
        <PhosphorIcon
          name="check-circle"
          className="mb-2 h-8 w-8 text-primary"
          aria-hidden="true"
        />
        <p className="font-semibold text-foreground">You&apos;re subscribed!</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Thanks for joining the Tippo newsletter.
        </p>
      </div>
    );
  }

  return (
    <aside
      className={cn(
        "rounded-xl border border-border bg-card p-5",
        className
      )}
      aria-label="Newsletter signup"
    >
      <h2 className="text-base font-semibold text-foreground">
        {variant === "blog-cta" ? "Stay in the loop" : "Tippo newsletter"}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {variant === "blog-cta"
          ? "Get tip-pooling insights and product updates in your inbox."
          : "Tip allocation insights and product updates for restaurant teams."}
      </p>
      <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
        <div className="grid gap-2">
          <Label htmlFor={`newsletter-email-${variant}`} className="sr-only">
            Email
          </Label>
          <Input
            id={`newsletter-email-${variant}`}
            type="email"
            placeholder="you@restaurant.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === "submitting"}
          />
        </div>
        {status === "error" && (
          <p className="text-sm text-destructive">{errorMessage}</p>
        )}
        <Button type="submit" size="sm" disabled={status === "submitting"}>
          {status === "submitting" ? "Subscribing…" : "Subscribe"}
        </Button>
      </form>
    </aside>
  );
}
